/**
 * SectionWidget — a section's nested grid (contentType "grid").
 *
 * WYSIWYG-first presentation (2026-07-20 redesign). At REST a section renders
 * EXACTLY like the published page: just its nested grid, no card, no border,
 * no header band. The editor chrome — a selection outline and a small floating
 * label tab (drag · label) — is an OVERLAY that appears only on hover
 * or selection, so it never consumes layout or breaks the published look
 * (Framer/Webflow pattern; the old always-on grey header + 28px band is gone,
 * which is why SECTION_CHROME_PX is now 0).
 *
 * Reorder: the section block is a GridItem in the page grid (press mode), so
 * its root owns the drag. The FLOATING TOOLBAR'S handle bubbles its pointerdown
 * → dragging it moves/reorders the block; the body wrapper stopPropagation so
 * editing a nested widget never starts a section drag. Clicking the tab
 * selects the block; its verbs (delete, reorder, gap…) live on the
 * SelectionActionBar — a ✕ beside a grab handle in a 22px tab was an
 * accidental-delete trap, worst on touch.
 *
 * Zoom: `contentProps.preview` (zoom-derived, PageNode) gates the nested grid.
 * `readOnly` (version preview) hides the interactive controls.
 *
 * Far zoom (`lod === "wireframe"`, via PageLodContext): the block stays a real
 * GridItem in the page grid (so it still reorders + accepts drag-adds), but its
 * CONTENT drops from the live nested grid to a single cheap SVG wireframe drawn
 * at the tile's exact pixel box — hundreds of section grids across the canvas
 * become one rasterized <img> each. This is where the zoomed-out perf win now
 * lives (the page grid itself no longer unmounts — user report 2026-07-21:
 * couldn't reorder/add sections in mock view when the whole grid was a skeleton).
 */
import { useLayoutEffect, useRef, useState } from "react";
import { useGridStore } from "@fredericrous/grid";
import { css, html } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";

import { usePageLod } from "../PageLodContext.js";
import { useReadOnly } from "../ReadOnlyContext.js";
import { sectionWireframe } from "../svgPreview.js";
import { GridWidget } from "./GridWidget.js";
import type { WidgetProps } from "./types.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";

const ACCENT = "rgb(59, 130, 246)";
const HOVER_LINE = "rgba(59, 130, 246, 0.35)";

// Longhand per react-strict-dom; nothing here relies on flex-grow.
const styles = css.create({
  // At rest: no background/border — identical to the published grid.
  // MERGE, don't double: the grid also paints a selection telegraph on this
  // section's box (`[data-grid-item][data-selected]`) at
  // `--grid-item-outline-offset` (the 0.9.4 seam; falls back to
  // `--grid-selected-outline-offset`, then 2px — gridSelection.css pins it).
  // Our own outline sits on the SAME edge (the tile fills the section, zero
  // gap — verified), so if we used a different offset the two rings ran
  // parallel a few px apart = a double stroke (user report 2026-07-21).
  // Reading the same var chain makes them land on the exact same line → one
  // crisp stroke (ours, the accent, paints on top). Same reasoning for the
  // hover line vs a child's hover ring. Radius 0 so the CORNERS coincide too
  // (a 4px radius here left "double corners", user report 2026-07-21).
  root: {
    position: "relative",
    width: "100%",
    height: "100%",
    outlineOffset:
      "var(--grid-item-outline-offset, var(--grid-selected-outline-offset, 2px))",
    borderRadius: 0,
  },
  rootOutline: (kind: "none" | "selected" | "hover") => ({
    outlineWidth: kind === "selected" ? 2 : kind === "hover" ? 1 : 0,
    outlineStyle: kind === "none" ? "none" : "solid",
    outlineColor: kind === "selected" ? ACCENT : HOVER_LINE,
  }),
  // Overflow opens while chrome shows (the outline + tab + any child ring
  // bleed aren't clipped); clips at rest to match publish.
  rootOpen: (open: boolean) => ({
    overflow: open ? "visible" : "hidden",
  }),
  wireframe: {
    display: "block",
    width: "100%",
    height: "100%",
    pointerEvents: "none",
  },
  body: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
  tab: {
    position: "absolute",
    top: -22,
    left: 0,
    zIndex: 6,
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    height: sizes.chip,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "0.5rem",
    paddingRight: "0.5rem",
    borderTopLeftRadius: "0.25rem",
    borderTopRightRadius: "0.25rem",
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: colors.contrastSurface,
    color: colors.onContrastSurface,
    fontSize: "0.7rem",
    fontWeight: typography.fontWeightSemibold,
    lineHeight: 1,
    cursor: "grab",
    userSelect: "none",
    boxShadow: shadows.sm,
  },
  tabSelected: {
    backgroundColor: ACCENT,
  },
  grip: {
    cursor: "grab",
  },
});

export function SectionWidget(props: WidgetProps) {
  const { itemId, gridId, contentProps } = props;
  const label = (contentProps?.["label"] as string | undefined) ?? "Section";
  const preview = contentProps?.["preview"] as boolean | undefined;
  const nestedGridId = contentProps?.["nestedGridId"] as string | undefined;
  const readOnly = useReadOnly();
  const lod = usePageLod();
  const [hover, setHover] = useState(false);

  // Far-zoom wireframe: the section's own nested-grid items (structure only) and
  // the tile's live pixel box. We MEASURE the mounted block rather than deriving
  // px from the grid config so the wireframe fills the section 1:1 and there's no
  // layout jump when the real nested grid takes over at closer zoom. offsetW/H is
  // the pre-zoom authoring box (the canvas zoom is a CSS transform on an ancestor,
  // which doesn't change offset metrics).
  const wireframe = lod === "wireframe";
  const wireframeItems = useGridStore((s) =>
    wireframe && nestedGridId ? s.grids[nestedGridId]?.items : undefined,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    if (!wireframe) return;
    const el = rootRef.current;
    if (!el) return;
    const measure = () => setBox({ w: el.offsetWidth, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [wireframe]);

  // This section BLOCK is selected in the PAGE grid (its own selection, not a
  // child's) → show the accent outline + toolbar.
  const sectionSelected = useGridStore(
    (s) =>
      s.selection?.gridId === gridId && s.selection.itemIds.includes(itemId),
  );
  // A widget INSIDE this section is being dragged/resized or holds the
  // selection — its telegraph rings bleed ~4px outside the child box, so the
  // section must not clip.
  const childEngaged = useGridStore(
    (s) =>
      !!nestedGridId &&
      (((s.interaction.gesture.kind === "drag" ||
        s.interaction.gesture.kind === "resize") &&
        s.interaction.gesture.gridId === nestedGridId) ||
        s.selection?.gridId === nestedGridId),
  );

  // Chrome shows on hover or selection; never in preview/publish or read-only
  // version-preview. Overflow opens then so the outline + overlay toolbar (and
  // any child ring bleed) aren't clipped; clips at rest to match publish.
  const showChrome = !readOnly && (hover || sectionSelected);
  const open = showChrome || childEngaged;

  const outline: "none" | "selected" | "hover" = sectionSelected
    ? "selected"
    : hover && !readOnly
      ? "hover"
      : "none";

  function selectSection() {
    useGridStore.getState().setSelection(gridId, [itemId]);
  }

  return (
    <html.div
      ref={rootRef}
      style={[styles.root, styles.rootOutline(outline), styles.rootOpen(open)]}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
    >
      {/* Body. At far zoom: a single static wireframe <img> (no nested grid
          mounted); pointerdown is NOT trapped, so grabbing anywhere on the
          section drags the block via the page grid. Otherwise: the live nested
          grid, flush to the block (WYSIWYG), with stopPropagation so
          editing/moving a nested widget never starts a section drag. */}
      {wireframe ? (
        box && (
          <html.img
            src={
              sectionWireframe(
                Object.values(wireframeItems ?? {}),
                box.w,
                box.h,
              ).uri
            }
            alt=""
            draggable={false}
            aria-hidden={true}
            style={styles.wireframe}
          />
        )
      ) : (
        <html.div
          style={styles.body}
          onPointerDown={(e: { stopPropagation: () => void }) =>
            e.stopPropagation()
          }
        >
          <GridWidget {...props} contentProps={{ ...contentProps, preview }} />
        </html.div>
      )}

      {/* Floating label tab — overlay (no layout cost), corner-anchored, shown
          only on hover/selection. The row itself is the section DRAG HANDLE:
          its pointerdown BUBBLES to the page-grid GridItem so dragging it
          reorders the block; a click (no move) selects it. ABOVE the
          section's top edge (a Webflow/Framer label tab), NOT over the
          content — sitting on top-left used to cover the start of the
          section's first line, so clicks meant for the heading hit the tab and
          selected the section instead of entering text edit (user report
          2026-07-20). Overflow is open while chrome shows, so the tab isn't
          clipped. */}
      {showChrome && (
        <html.div
          role="toolbar"
          // "…handle", not "…section": the selection bar is the toolbar named
          // after the section; two toolbars with one name would read as twins.
          aria-label={`${label} section handle`}
          onClick={selectSection}
          style={[styles.tab, sectionSelected && styles.tabSelected]}
        >
          <html.span aria-hidden={true} style={styles.grip}>
            ⠿
          </html.span>
          <html.span>{label}</html.span>
        </html.div>
      )}
    </html.div>
  );
}
